import { useSnackbar } from "notistack"

import { unknownErrorToString } from "../../utils/errors"
import { useTranslation } from "react-i18next"
import { useLazyGetStorageFileDownloadUrlQuery } from "../api"

export const useFileDownload = () => {
  const translations = useTranslations()
  const { enqueueSnackbar } = useSnackbar()

  const [getDownloadUrl] = useLazyGetStorageFileDownloadUrlQuery()

  const handleError = (error: any) => {
    const message = unknownErrorToString(error)
    enqueueSnackbar(message, { variant: "error" })
  }

  const downloadFile = async (fileId: string) => {
    try {
      const res = await getDownloadUrl(fileId).unwrap()
      const { url, filename } = res
      if (!url) throw new Error("Missing download URL")
      // fetch file as blob so we can force download with filename
      const response = await fetch(url)
      const blob = await response.blob()

      const blobUrl = window.URL.createObjectURL(blob)

      const a = document.createElement("a")
      a.href = blobUrl
      a.download = filename || "download"
      document.body.appendChild(a)
      a.click()

      a.remove()
      window.URL.revokeObjectURL(blobUrl)

      enqueueSnackbar(translations.downloadSuccess, {
        variant: "success",
      })

      return true
    } catch (err) {
      handleError(err)

      enqueueSnackbar(translations.downloadFailed, {
        variant: "error",
      })

      return false
    }
  }

  const openFileInBrowser = async (fileId: string) => {
    try {
      const res = await getDownloadUrl(fileId).unwrap()
      if (!res?.url) throw new Error("Missing file URL")
      window.open(res.url, "_blank", "noopener,noreferrer")
      return true
    } catch (err) {
      handleError(err)
      enqueueSnackbar(translations.downloadFailed, {
        variant: "error",
      })

      return false
    }
  }
  return { downloadFile, openFileInBrowser }
}

const defaultTranslations = {
  downloadSuccess: "Download started",
  downloadFailed: "Failed to download file",
}

const useTranslations = (defaults = defaultTranslations) => {
  const { t } = useTranslation("storage-file")

  return {
    downloadSuccess: t("downloadSuccess", defaults.downloadSuccess),
    downloadFailed: t("downloadFailed", defaults.downloadFailed),
  }
}
